import { eChannelType } from '../../../../../../../../providers/SocketsProvider/Sockets.enums';
import {
  resolveScheduledFailedNotice,
  withoutRetryAction,
} from '../../../../../../../../utils/metaErrorNotice';
import { tMetaErrorNotice } from '../../../../../../../../utils/metaErrorNotice.types';
import { EventType, MessageState } from '../../../../../../Chat.enum';
import { scheduledTemplateOutcome } from '../../../../../../Chat.functions';
import {
  eScheduledTemplateOutcome,
  tChatEvent,
} from '../../../../../../Chat.types';
import chatUtils from '../../../../../../Chat.utils';
import { resolveOutgoingMetaErrorNotice } from '../MessageBubble/MessageBubbleV2.functions';

/**
 * INT-1505: which sent message each scheduled card turned into, and the
 * reverse. A card with a partner gives way to it: the card is hidden and the
 * sent message carries the "programado por" legend.
 */
export type tScheduledPairs = {
  sentByFollowUpId: ReadonlyMap<string, tChatEvent>;
  followUpBySentId: ReadonlyMap<string, tChatEvent>;
};

// The cron picks a due card up within its minute, so a send stamped slightly
// before the trigger (clock skew between services) still belongs to it.
const SENT_BEFORE_TRIGGER_TOLERANCE_MS = 60 * 1000;
// A regular follow-up goes out within the minute it comes due; a few minutes
// covers a slow cron run without reaching a later manual send.
const MESSAGE_SENT_AFTER_TRIGGER_WINDOW_MS = 5 * 60 * 1000;
// A template waits for the campaign launch cron, and the due query reaches a
// day back. Past a day the send cannot be this card's.
const TEMPLATE_SENT_AFTER_TRIGGER_WINDOW_MS = 24 * 60 * 60 * 1000;

function normalizeText(text: string | null | undefined): string {
  return (text ?? '').replace(/\s+/g, ' ').trim();
}

function isScheduledTemplate(followUp: tChatEvent): boolean {
  return Boolean(followUp.isWapiTemplate || followUp.followUp?.campaignId != null);
}

// A card the UI still shows as pending has not been sent, so no message on
// screen can be its send: a manual message with the same text must not hide it.
// Same rule as FollowUpBubble's pending state.
function isProcessed(followUp: tChatEvent): boolean {
  return (
    Boolean(followUp.followUp?.consumed) ||
    scheduledTemplateOutcome(followUp) !== eScheduledTemplateOutcome.PENDING
  );
}

function textMatches(followUp: tChatEvent, sent: tChatEvent): boolean {
  const scheduledText = normalizeText(followUp.content?.text);
  const sentText = normalizeText(sent.content?.text);

  if (!scheduledText) {
    // An attachment-only message: the first message it expands into carries
    // an attachment and no text.
    return !sentText && Boolean(sent.content?.attachments?.length);
  }
  if (sentText === scheduledText) return true;
  // The card keeps the template body with its variables filled in; the sent
  // template may add its header text around it.
  return isScheduledTemplate(followUp) && sentText.includes(scheduledText);
}

/**
 * INT-1505: pairs every scheduled card with the message it became.
 *
 * INT-1500 U11 (INT-1525): messaging links them (`followUp.messageId`), and
 * the link wins. A linked card pairs with the event it names, whatever its
 * text, author, time or the card's state; while that event is not in the
 * thread it pairs with nothing, and nothing else may take that event.
 *
 * Cards linked before the field existed have no link. Such a card, once the
 * backend processed it, is paired with the earliest outgoing message of its
 * kind (MESSAGE, or TEMPLATE for a template) that has the same author and the
 * same text and was sent in its window (minutes for a message, a day for a
 * template). Each message pairs at most once. A pending card, or one with no
 * partner, stays as it is: nothing is hidden unless its sent message is on
 * screen.
 */
export function pairScheduledMessages(events: tChatEvent[]): tScheduledPairs {
  const sentByFollowUpId = new Map<string, tChatEvent>();
  const followUpBySentId = new Map<string, tChatEvent>();

  const eventsById = new Map(events.map((event) => [event.id, event]));
  const unlinked: tChatEvent[] = [];
  for (const event of events) {
    if (event.type !== EventType.FOLLOW_UP) continue;
    const messageId = event.followUp?.messageId;
    if (!messageId) {
      unlinked.push(event);
      continue;
    }
    const partner = eventsById.get(messageId);
    if (
      partner &&
      partner.type !== EventType.FOLLOW_UP &&
      !followUpBySentId.has(partner.id)
    ) {
      sentByFollowUpId.set(event.id, partner);
      followUpBySentId.set(partner.id, event);
    }
  }

  const followUps = unlinked
    .filter(
      (event) =>
        event.createdBy &&
        isProcessed(event) &&
        !Number.isNaN(Date.parse(event.followUp?.triggerDate ?? '')),
    )
    .sort(
      (a, b) =>
        Date.parse(a.followUp!.triggerDate) - Date.parse(b.followUp!.triggerDate),
    );

  for (const followUp of followUps) {
    const triggerAt = Date.parse(followUp.followUp!.triggerDate);
    const isTemplate = isScheduledTemplate(followUp);
    const sentType = isTemplate ? EventType.TEMPLATE : EventType.MESSAGE;
    const windowMs = isTemplate
      ? TEMPLATE_SENT_AFTER_TRIGGER_WINDOW_MS
      : MESSAGE_SENT_AFTER_TRIGGER_WINDOW_MS;

    let partner: tChatEvent | undefined;
    let partnerAt = Infinity;
    for (const candidate of events) {
      if (candidate.type !== sentType) continue;
      if (!candidate.message?.fromMe) continue;
      if (candidate.createdBy !== followUp.createdBy) continue;
      if (followUpBySentId.has(candidate.id)) continue;

      const sentAt = Date.parse(candidate.createdAt);
      if (sentAt < triggerAt - SENT_BEFORE_TRIGGER_TOLERANCE_MS) continue;
      if (sentAt > triggerAt + windowMs) continue;
      if (sentAt >= partnerAt) continue;
      if (!textMatches(followUp, candidate)) continue;

      partner = candidate;
      partnerAt = sentAt;
    }

    if (partner) {
      sentByFollowUpId.set(followUp.id, partner);
      followUpBySentId.set(partner.id, followUp);
    }
  }

  return { sentByFollowUpId, followUpBySentId };
}

export type tScheduledDateLabels = {
  /** "hoy {{time}}" */
  today: (time: string) => string;
  /** "mañana {{time}}" */
  tomorrow: (time: string) => string;
  /** Twelve lower-case abbreviations, no period: "ene" … "dic". */
  months: readonly string[];
};

/** Local midnight of a date, as epoch milliseconds. */
export function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/** "18:05": the 24-hour local clock time of a date. */
export function clockTime(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(
    date.getMinutes(),
  ).padStart(2, '0')}`;
}

/** "25 sep", or "25 sep 2025" when the year is not the current one. */
function dayAndMonth(value: Date, options: { now: Date; months: readonly string[] }): string {
  const month = options.months[value.getMonth()] ?? '';
  const year =
    value.getFullYear() === options.now.getFullYear() ? '' : ` ${value.getFullYear()}`;
  return `${value.getDate()} ${month}${year}`;
}

/**
 * INT-1505, voice and tone: "hoy 18:00" and "mañana 09:00" for the near
 * future, "22 sep 10:15" within the current year, "22 sep 2025 10:15" for any
 * other year. 24-hour clock, local time.
 */
export function formatScheduledDate(
  date: string,
  options: { now: Date; labels: tScheduledDateLabels },
): string {
  const { now, labels } = options;
  const value = new Date(date);
  if (Number.isNaN(value.getTime())) return '';

  const time = clockTime(value);

  const dayDiff = Math.round((startOfDay(value) - startOfDay(now)) / 86_400_000);
  if (dayDiff === 0) return labels.today(time);
  if (dayDiff === 1) return labels.tomorrow(time);

  return `${dayAndMonth(value, { now, months: labels.months })} ${time}`;
}

/**
 * INT-1500 U7 (D7): a day as a hold's body says it: "25 sep", or "25 sep 2025"
 * in another year. Never "hoy": the copy reads "el {{failedOn}}".
 */
export function formatScheduledDay(
  date: string,
  options: { now: Date; months: readonly string[] },
): string {
  const value = new Date(date);
  if (Number.isNaN(value.getTime())) return '';

  return dayAndMonth(value, options);
}

/**
 * INT-1500 U9: the notice a failed scheduled card shows, resolved the way a
 * failed message's is (resolveOutgoingMetaErrorNotice) from its error row
 * (contract C4): Meta's own code when Meta refused it (e.g. 132018), our
 * refusal with its hold facts (U7, U8), or the Meta code behind the chat code.
 * Anything else gets the generic scheduled notice with its code, never "El
 * envío programado no pudo completarse". No card offers "Reintentar": a
 * scheduled send is not retried from the chat (D7).
 *
 * The chat's refusals (codes 1016-1018) are the caller's: they keep their own
 * copy (findOutboundRefusal).
 */
export function resolveScheduledFailureNotice(
  followUp: Pick<tChatEvent, 'errors'>,
  channel: eChannelType | string | undefined,
): tMetaErrorNotice {
  const { errors } = followUp;
  const error = chatUtils().parseErrors(errors?.map(({ code }) => ({ code })));
  const notice = resolveOutgoingMetaErrorNotice(
    { fromMe: true, state: MessageState.ERROR, error, errors },
    channel,
  );
  if (notice) return withoutRetryAction(notice);

  const row = errors?.[0];
  return resolveScheduledFailedNotice({
    code: row?.code,
    failureCode: row?.error?.failureCode,
    context: row?.error?.context,
  });
}

